import { useMemo, useState } from 'react'
// import { Link } from 'react-router-dom'  // used only by the commented-out detail panel
import { useTranslation } from 'react-i18next'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Num } from '@/components/forge/primitives'
import {
  useDeleteRole,
  usePermissionCatalogue,
  useRoles,
} from '@/features/rbac/queries'
import { usePermissionScopes } from '@/features/auth/permissions'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatInteger } from '@/lib/format'
import { ON_TINT_CLASS, TINT_CLASS } from '@/lib/tone'
import { useToast } from '@/providers/ToastProvider'
import { cn } from '@/lib/cn'
import { PermissionMatrixDialog } from './PermissionMatrixDialog'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { RoleEditorDrawer } from './RoleEditorDrawer'

/**
 * Settings → Roles & permissions.
 *
 * DEMO. See `DEMO-MODE.md` §8.15.
 *
 * ── The modules are this product's modules ────────────────────────────────
 * The catalogue is the navigation rail: every permission names a screen with a
 * route, an icon and a name the product already gives it. The design invents its
 * own list — "Root Cause Analysis", "Integrations" — half of which does not
 * exist here, and a permission naming a screen nobody can open is one nobody can
 * ever test.
 *
 * ── The grid has a consequence you can look at ────────────────────────────
 * Each module declares the feature that means "can reach it at all", and the
 * role detail draws the rail as somebody holding that role would find it. Strip
 * `anomalies.view` and Anomalies leaves the list while you watch. Without that,
 * a permission grid is a wall of checkboxes nobody can check the effect of —
 * which is what the prototype's is.
 *
 * ── The eight system roles are the vocabulary, not a copy of it ───────────
 * `common:roles` is what the Users table prints, what the create dialog offers
 * and what the bulk import validates against. This page lists exactly those and
 * cannot edit or delete them: removing `maintenanceTechnician` would leave two
 * people holding a role that no longer exists. Duplicate makes an editable copy,
 * which is the design's own answer and the only safe one.
 *
 * ── How many people hold a role is counted ────────────────────────────────
 * The design writes `users: 3` beside each role, free to disagree with the
 * directory. Here it is a count over the registry the Users screen reads, and
 * the names are listed and open.
 */

export function RolesPage() {
  const { t } = useTranslation(['app', 'common'])
  useDocumentTitle(`${t('settings.roles.title')} · ${t('common:brand.name')}`)

  const { toast } = useToast()

  const [selectedId, setSelectedId] = useState(null)
  const [editing, setEditing] = useState(null)
  const [matrix, setMatrix] = useState(false)
  /** The role a confirmation is open for. */
  const [removing, setRemoving] = useState(null)

  const roles = useRoles()
  const catalogue = usePermissionCatalogue()
  const remove = useDeleteRole()

  const items = useMemo(() => roles.data?.items ?? [], [roles.data])
  const grant = usePermissionScopes('users.manage')

  /* Names for the plants that own roles. One read per distinct plant, cached. */
  const plantTargets = useMemo(
    () => items.filter((role) => role.plantId).map((role) => ({ tier: 'site', id: role.plantId })),
    [items],
  )
  const plants = usePlaceNodes(plantTargets)
  const plantName = useMemo(
    () => new Map(plants.data.items.map((node) => [node.id, node.name])),
    [plants.data],
  )
  const scopeLabel = (role) =>
    role.plantId
      ? (plantName.get(role.plantId) ?? role.plantId)
      : t('settings.roles.scopeAccount')
  /* `covers_plant` on the server: an account grant changes any custom role, a
     plant grant only its own plants' roles. */
  const canChange = (role) =>
    !role.isSystem &&
    (grant.accountLevel || (role.plantId !== null && grant.plantIds.has(role.plantId)))
  const counts = roles.data?.counts ?? { system: 0, custom: 0 }

  /* Derived rather than held in an effect: the first role is the selection until
     somebody picks another, and a role that has just been created should be the
     one on screen. */
  const selected = items.find((role) => role.id === selectedId) ?? items[0] ?? null

  /* The catalogue, flattened once, so the detail panel can name a role's
     permission ids without walking ten modules per row. */
  const permissionById = useMemo(() => {
    const map = new Map()
    for (const module of catalogue.data?.modules ?? []) {
      for (const permission of module.permissions) {
        map.set(permission.id, { ...permission, moduleName: module.name })
      }
    }
    return map
  }, [catalogue.data])

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <h1 className="m-0 text-headline">{t('settings.roles.title')}</h1>
          <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
            {t('settings.roles.intro')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Every role against every permission. Disabled until both halves of
              the grid have arrived — an axis-less matrix is an empty box. */}
          <ForgeButton
            disabled={!items.length || !catalogue.data}
            onClick={() => setMatrix(true)}
          >
            <Icon name="board" size="xs" />
            {t('settings.roles.matrix')}
          </ForgeButton>
          <ForgeButton variant="primary" onClick={() => setEditing({ mode: 'create' })}>
            <Icon name="plus" size="sm" />
            {t('settings.roles.create')}
          </ForgeButton>
        </div>
      </header>

      {roles.isPending ? (
        <SkeletonRows count={5} />
      ) : roles.isError ? (
        <ErrorState
          error={roles.error}
          title={t('settings.roles.loadFailed')}
          onRetry={roles.refetch}
        />
      ) : (
        <>
          {/* Two counts, not four. `total` is the sum of these and said twice;
              `assigned` counted how many people hold each role, and the roles
              endpoint does not report that — it would be a request per role. */}
          <div className="mb-4 grid grid-cols-2 gap-2.5">
            {['system', 'custom'].map((key) => (
              <div key={key} className="rounded-sm border border-hairline bg-card px-3.5 py-3">
                <Num className="block text-display-sm text-ink">{formatInteger(counts[key])}</Num>
                <span className="eyebrow mt-1.5 block">{t(`settings.roles.facets.${key}`)}</span>
                <span className="mt-0.5 block text-micro font-normal text-ink-mute">
                  {t(`settings.roles.facetHints.${key}`)}
                </span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 max-xl:grid-cols-1">
            <Panel flush className="overflow-hidden">
              <div className="border-b border-hairline px-3.5 py-2.5">
                <Eyebrow>{t('settings.roles.listLabel')}</Eyebrow>
              </div>
              <ul className="m-0 flex max-h-[34rem] list-none flex-col overflow-y-auto p-0">
                {items.map((role) => (
                  <li key={role.id}>
                    <button
                      type="button"
                      aria-current={selected?.id === role.id ? 'true' : undefined}
                      onClick={() => setSelectedId(role.id)}
                      className={cn(
                        'flex w-full items-center gap-2.5 border-s-[3px] px-3.5 py-2.5 text-start transition-colors',
                        selected?.id === role.id
                          ? 'border-s-forge tint-forge'
                          : 'border-s-transparent hover:bg-sunken',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          {/* The server's own name, for both kinds. A system
                              role used to be a translated key from
                              `common:roles`, because the demo's eight were the
                              product's own vocabulary. These are database rows
                              and nothing translates them. */}
                          <span className="truncate text-body-sm font-semibold text-ink">
                            {role.name}
                          </span>
                          <TypePill system={role.isSystem} />
                        </span>
                        <span className="mt-0.5 block truncate text-micro font-normal text-ink-dim">
                          <Num>
                            {t('settings.roles.permissionCount', {
                              count: role.permissionIds.length,
                            })}
                          </Num>
                          {' · '}
                          {scopeLabel(role)}
                        </span>
                      </span>
                      <Icon name="right" size="xs" className="mirror-rtl text-ink-mute" />
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>

            {selected ? (
              <RoleDetail
                role={selected}
                scope={scopeLabel(selected)}
                canChange={canChange(selected)}
                permissionById={permissionById}
                onEdit={() => setEditing({ mode: 'edit', role: selected })}
                onDelete={() => {
                  remove.reset()
                  setRemoving(selected)
                }}
              />
            ) : null}
          </div>
        </>
      )}

      <RoleEditorDrawer
        request={editing}
        onClose={() => setEditing(null)}
        onSaved={(role) => setSelectedId(role.id)}
      />

      <PermissionMatrixDialog
        open={matrix}
        roles={items}
        modules={catalogue.data?.modules ?? []}
        onClose={() => setMatrix(false)}
      />

      {/* ── Removal, confirmed ───────────────────────────────────────────
          The server refuses to delete a role anybody still holds, and that is
          the right call — no cascade, no orphaning. It is also not knowable in
          advance, because the list carries no holder count. So the dialog says
          what the rule is before the click, and reports the refusal plainly if
          it lands. */}
      <ForgeDialog
        open={Boolean(removing)}
        onOpenChange={(next) => {
          if (!next) setRemoving(null)
        }}
        size="sm"
        title={removing ? t('settings.roles.deleteTitle', { name: removing.name }) : ''}
        footer={
          <>
            <ForgeButton
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(removing.id, {
                  onSuccess: () => {
                    toast(t('settings.roles.deleted'), 'ok')
                    setSelectedId(null)
                    setRemoving(null)
                  },
                })
              }
            >
              {t('settings.roles.deleteConfirm')}
            </ForgeButton>
            <ForgeButton onClick={() => setRemoving(null)}>
              {t('common:actions.cancel')}
            </ForgeButton>
          </>
        }
      >
        {remove.isError ? (
          <div
            role="alert"
            className="mb-4 rounded-sm border border-hairline stripe-red bg-card p-3.5"
          >
            <p className="m-0 text-control text-on-red">{t('settings.roles.deleteRefused')}</p>
            <p className="mt-1 mb-0 text-body-sm text-ink-dim">
              {t('settings.roles.deleteRefusedBody')}
            </p>
          </div>
        ) : null}
        <p className="m-0 text-body text-ink-dim">{t('settings.roles.deleteBody')}</p>
      </ForgeDialog>
    </div>
  )
}

/**
 * What one role permits, read only.
 *
 * ── Edit and Delete, on a custom role only ───────────────────────────────
 * A system role refuses both, and `isSystem` is on the response — so the buttons
 * are not offered rather than offered and refused, and the panel says why.
 *
 * The other rule cannot be answered here: a role somebody HOLDS may be renamed
 * but may not have its permissions changed, and may not be deleted at all. The
 * list carries no holder count, so both are stated as rules before the click and
 * reported plainly if the server refuses. The editor goes further — it offers
 * the save that would have worked. See `RoleEditorDrawer`.
 *
 * Duplicate is not here. It made an editable copy of a system role, which
 * mattered when those were the product's own eight and the only way to get a
 * starting point. Creating a role from the grid is two clicks, and a copy button
 * that silently drops permissions the API would refuse is worse than no copy.
 */
function RoleDetail({ role, scope, canChange, permissionById, onEdit, onDelete }) {
  const { t } = useTranslation(['app', 'common'])

  /* Grouped by module, in the catalogue's own order, so the panel reads like the
     grid that granted them rather than as a flat list of seventeen. */
  const byModule = useMemo(() => {
    const map = new Map()
    for (const id of role.permissionIds) {
      const permission = permissionById.get(id)
      if (!permission) continue
      if (!map.has(permission.moduleName)) map.set(permission.moduleName, [])
      map.get(permission.moduleName).push(permission)
    }
    return [...map.entries()]
  }, [role, permissionById])

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="m-0 flex items-center gap-2 text-title">
            {role.name}
            <TypePill system={role.isSystem} />
          </h2>
          <p className="mt-1 mb-0 max-w-[60ch] text-body-sm text-ink-dim">{role.description}</p>
          <p className="mt-1 mb-0 flex items-center gap-1.5 text-micro text-ink-mute">
            <Icon name="factory" size="xs" />
            {scope}
          </p>
        </div>

        {!canChange ? null : (
          <div className="flex shrink-0 flex-wrap gap-2">
            <ForgeButton size="sm" onClick={onEdit}>
              <Icon name="pencil" size="sm" />
              {t('common:actions.edit')}
            </ForgeButton>
            <ForgeButton size="sm" variant="ghost" onClick={onDelete} className="hover:text-on-red">
              <Icon name="trash" size="sm" />
              {t('common:actions.delete')}
            </ForgeButton>
          </div>
        )}
      </div>

      {role.isSystem ? (
        <p className="m-0 rounded-sm border border-hairline stripe-amber bg-card px-3 py-2 text-body-sm text-on-amber">
          {t('settings.roles.systemLocked')}
        </p>
      ) : !canChange ? (
        <p className="m-0 rounded-sm border border-hairline stripe-amber bg-card px-3 py-2 text-body-sm text-on-amber">
          {t('settings.roles.outsideGrant')}
        </p>
      ) : null}

      {byModule.length ? (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {byModule.map(([moduleName, permissions]) => (
            <li key={moduleName}>
              <Eyebrow className="mb-1.5">{moduleName}</Eyebrow>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {permissions.map((permission) => (
                  <li key={permission.id} className="flex items-start gap-2">
                    <Icon name="check" size="xs" className="mt-1 shrink-0 text-on-forge" />
                    <span className="min-w-0">
                      <span className="block text-body-sm text-ink">{permission.name}</span>
                      <span className="block text-micro font-normal text-ink-mute">
                        {permission.description}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-body-sm text-ink-dim">{t('settings.roles.noneGranted')}</p>
      )}
    </Panel>
  )
}

/* ── `RoleName` is gone ───────────────────────────────────────────────────
   A system role's name used to be a translated key from `common:roles`, because
   the demo's eight system roles WERE the product's own vocabulary. The real ones
   are database rows seeded server-side, and nothing translates them — so both
   kinds print the name the server sent.
   ─────────────────────────────────────────────────────────────────────── */

// /** A system role's name is a translated key; a custom one's is what somebody typed. */
// function RoleName({ role }) {
//   const { t } = useTranslation(['app', 'common'])
//   return <>{role.system ? t(`common:roles.${role.id}`) : role.name}</>
// }

function TypePill({ system }) {
  const { t } = useTranslation('app')
  return (
    <span
      className={cn(
        'shrink-0 rounded-pill px-2 py-[2px] text-micro whitespace-nowrap',
        system ? 'bg-sunken text-ink-dim' : cn(TINT_CLASS.forge, ON_TINT_CLASS.forge),
      )}
    >
      {t(system ? 'settings.roles.system' : 'settings.roles.custom')}
    </span>
  )
}

/* ══ DETAIL ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════
   THE OLD DETAIL PANEL, AND WHAT IT DEPENDED ON

   This drew a role against the invented catalogue — the navigation rail, with
   authored feature names and a declared `gate` per module — and offered Edit,
   Duplicate and Delete beside it.

   None of that survives the real contract. The catalogue is now ten seeded
   modules of capabilities, with no gate and no mapping onto screens, so the
   nav-rail preview has nothing to preview. And all three actions carry refusals
   the panel cannot predict from the list response: a system role refuses edit
   and delete, and a role somebody holds refuses a permission change and refuses
   deletion — but the holder count is not on the response, so the panel cannot
   know which buttons would work before one is pressed.

   Kept rather than deleted: the layout is the design's and is worth restoring
   when the role response carries a holder count and the scope model is settled.
   ══════════════════════════════════════════════════════════════════════════ */

// function RoleDetail({ role, catalogue, onDuplicate, onEdit, onDelete, deleting }) {
//   const { t } = useTranslation(['app', 'common'])
//   const total = catalogue.reduce((sum, module) => sum + module.features.length, 0)
//
//   return (
//     <div className="flex flex-col gap-4">
//       <Panel className="p-4">
//         <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5">
//           <div className="min-w-0">
//             <h2 className="m-0 flex items-center gap-2.5 text-title">
//               <RoleName role={role} />
//               <TypePill system={role.system} />
//             </h2>
//             <p className="mt-1.5 mb-0 max-w-[60ch] text-body-sm text-ink-dim">
//               {role.system
//                 ? t(`settings.roles.descriptions.${role.id}`)
//                 : (role.description ?? t('settings.roles.noDescription'))}
//             </p>
//           </div>
//           <div className="flex shrink-0 flex-wrap gap-2">
//             <ForgeButton size="sm" onClick={onDuplicate}>
//               {t('settings.roles.duplicate')}
//             </ForgeButton>
//             {!role.system ? (
//               <>
//                 <ForgeButton size="sm" onClick={onEdit}>
//                   {t('common:actions.edit')}
//                 </ForgeButton>
//                 <ForgeButton size="sm" disabled={deleting} onClick={onDelete}>
//                   {t('common:actions.delete')}
//                 </ForgeButton>
//               </>
//             ) : null}
//           </div>
//         </div>
//
//         <dl className="mt-4 mb-0 grid grid-cols-3 gap-3.5 border-t border-subtle pt-3.5 max-sm:grid-cols-2">
//           {[
//             ['permissions', `${role.grantCount} / ${total}`],
//             ['modules', `${role.moduleCount} / ${catalogue.length}`],
//             ['holders', formatInteger(role.userCount)],
//           ].map(([key, value]) => (
//             <div key={key}>
//               <dt className="eyebrow">{t(`settings.roles.stat.${key}`)}</dt>
//               <dd className="m-0 mt-1">
//                 <Num className="text-title">{value}</Num>
//               </dd>
//             </div>
//           ))}
//         </dl>
//
//         {role.system ? (
//           <p className="mt-3.5 mb-0 max-w-[60ch] text-micro font-normal text-ink-dim">
//             {t('settings.roles.systemNote')}
//           </p>
//         ) : null}
//       </Panel>
//
//       {/* The consequence. A grid without this is a wall of checkboxes nobody can
//           check the effect of. */}
//       <Panel className="p-4">
//         <Eyebrow className="mb-2">{t('settings.roles.reaches')}</Eyebrow>
//         {role.reaches.length ? (
//           <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
//             {catalogue
//               .filter((module) => role.reaches.includes(module.id))
//               .map((module) => (
//                 <li key={module.id}>
//                   <Link
//                     to={module.to}
//                     className="flex items-center gap-1.5 rounded-pill tint-green px-2.5 py-1 text-micro text-on-green"
//                   >
//                     <Icon name={module.icon} size="xs" />
//                     {t(`screens.${module.id}`)}
//                   </Link>
//                 </li>
//               ))}
//           </ul>
//         ) : (
//           <p className="m-0 text-body-sm text-on-amber">{t('settings.roles.reachesNothing')}</p>
//         )}
//         <p className="mt-2.5 mb-0 max-w-[60ch] text-micro font-normal text-ink-mute">
//           {t('settings.roles.reachesNote')}
//         </p>
//       </Panel>
//
//       <Panel flush className="overflow-hidden">
//         <div className="flex items-center gap-3 border-b border-hairline px-3.5 py-2.5">
//           <Eyebrow className="flex-1">{t('settings.roles.granted')}</Eyebrow>
//           <Num className="text-micro font-normal text-ink-dim">
//             {role.grantCount} / {total}
//           </Num>
//         </div>
//         <ul className="m-0 flex max-h-[26rem] list-none flex-col overflow-y-auto p-0">
//           {catalogue.map((module) => {
//             const on = module.features.filter((f) => role.grant[`${module.id}.${f}`])
//             return (
//               <li key={module.id} className="border-b border-subtle px-3.5 py-3 last:border-0">
//                 <div className="flex items-center gap-2.5">
//                   <Icon
//                     name={module.icon}
//                     size="sm"
//                     className={on.length ? 'text-on-forge' : 'text-ink-mute'}
//                   />
//                   <span className="flex-1 text-body-sm font-semibold text-ink">
//                     {t(`screens.${module.id}`)}
//                   </span>
//                   <Num className="text-micro font-normal text-ink-dim">
//                     {on.length} / {module.features.length}
//                   </Num>
//                 </div>
//                 <ul className="m-0 mt-2 flex list-none flex-wrap gap-1.5 p-0 ps-7">
//                   {module.features.map((feature) => {
//                     const granted = Boolean(role.grant[`${module.id}.${feature}`])
//                     return (
//                       <li
//                         key={feature}
//                         className={cn(
//                           'rounded-pill px-2.5 py-[3px] text-micro whitespace-nowrap',
//                           granted
//                             ? cn(TINT_CLASS.green, ON_TINT_CLASS.green)
//                             : 'bg-sunken text-ink-mute line-through decoration-ink-mute',
//                         )}
//                       >
//                         {t(`settings.roles.features.${feature}`)}
//                       </li>
//                     )
//                   })}
//                 </ul>
//               </li>
//             )
//           })}
//         </ul>
//       </Panel>
//     </div>
//   )
// }
